/**
 * 💳 SERVICIO DE SUSCRIPCIONES
 * 
 * Gestiona las suscripciones de usuarios con PayPal
 * Precio: $2 USD por acceso completo
 */

import { db, auth } from '../firebase';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';

// 💰 Configuración de precios
export const SUBSCRIPTION_CONFIG = {
  PRICE: 2.00,
  CURRENCY: 'USD',
  DURATION_DAYS: 30 // Suscripción mensual
};

// 👑 Lista de emails de administradores con acceso completo
const ADMIN_EMAILS = [
  'ceed.educacion@gmail.com',
  // Agrega más emails de admin aquí si es necesario
];

/**
 * Verificar si el usuario actual es administrador
 */
export const isAdmin = () => {
  const user = auth.currentUser;
  if (!user || !user.email) return false;
  
  return ADMIN_EMAILS.includes(user.email.toLowerCase());
};

/**
 * Verificar si un usuario tiene suscripción activa
 */
export const checkSubscription = async (userId) => {
  if (!userId) return false;
  
  // 👑 Administradores tienen acceso automático
  if (isAdmin()) {
    console.log('✅ Acceso de administrador detectado');
    return true;
  }
  
  try {
    const userDoc = await getDoc(doc(db, 'subscriptions', userId));
    
    if (!userDoc.exists()) return false;
    
    const data = userDoc.data();
    const now = new Date();
    const expiryDate = data.expiryDate?.toDate();
    
    return data.isActive && expiryDate && expiryDate > now;
  } catch (error) {
    console.error('Error checking subscription:', error);
    return false;
  }
};

/**
 * Obtener información de suscripción del usuario
 */
export const getSubscriptionInfo = async (userId) => {
  if (!userId) return null;
  
  try {
    const userDoc = await getDoc(doc(db, 'subscriptions', userId));
    
    if (!userDoc.exists()) return null;
    
    return userDoc.data();
  } catch (error) {
    console.error('Error getting subscription info:', error);
    return null;
  }
};

/**
 * Crear o actualizar suscripción después del pago
 */
export const activateSubscription = async (userId, paypalOrderId, paypalPayerId) => {
  if (!userId) throw new Error('User ID requerido');
  
  try {
    const now = new Date();
    const expiryDate = new Date(now.getTime() + (SUBSCRIPTION_CONFIG.DURATION_DAYS * 24 * 60 * 60 * 1000));
    
    const subscriptionData = {
      userId,
      isActive: true,
      startDate: serverTimestamp(),
      expiryDate: expiryDate,
      paypalOrderId,
      paypalPayerId,
      amount: SUBSCRIPTION_CONFIG.PRICE,
      currency: SUBSCRIPTION_CONFIG.CURRENCY,
      lastUpdated: serverTimestamp()
    };
    
    await setDoc(doc(db, 'subscriptions', userId), subscriptionData, { merge: true });
    
    // Guardar historial de pago
    await setDoc(doc(db, 'payments', paypalOrderId), {
      userId,
      amount: SUBSCRIPTION_CONFIG.PRICE,
      currency: SUBSCRIPTION_CONFIG.CURRENCY,
      status: 'completed',
      paypalOrderId,
      paypalPayerId,
      timestamp: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error('Error activating subscription:', error);
    throw error;
  }
};

/**
 * Cancelar suscripción
 */
export const cancelSubscription = async (userId) => {
  if (!userId) throw new Error('User ID requerido');
  
  try {
    await updateDoc(doc(db, 'subscriptions', userId), {
      isActive: false,
      cancelledAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error('Error cancelling subscription:', error);
    throw error;
  }
};
